/**
 * The decisions behind the Notifications screen (org-admin#131), without React.
 *
 * Split out because this repo has no DOM tests: a label, a threshold or a tab
 * rule reachable only through a rendered component is one nothing asserts.
 *
 * ── Labels fall back to what the server sent ─────────────────────────
 *
 * auth owns the vocabulary for statuses, delivery results, reasons and sources,
 * and adds to it without telling this app. Every mapping here therefore renders
 * an unknown value as itself rather than as nothing, so a new reason shows up
 * as `some-new-reason` instead of a blank cell.
 */
import type { NotificationSummary, SendStatus, DeliveryStatus } from './api.js';

export type Tone = 'good' | 'warn' | 'danger' | 'muted';

// ── Statuses ─────────────────────────────────────────────────────────

/** Plain words, as #131 asks: Sent, Suppressed (opted out), Refused, Failed, Queued. */
export function statusLabel(status: string, reason?: string | null): string {
  switch (status as SendStatus) {
    case 'queued':
      return 'Queued';
    case 'sending':
      return 'Sending';
    case 'sent':
      return 'Sent';
    case 'suppressed':
      /* `opted-out` is the only suppression reason today. Naming it in the
         status saves a click for the question support actually has: "did they
         unsubscribe?" */
      return reason === 'opted-out' ? 'Suppressed (opted out)' : 'Suppressed';
    case 'refused':
      return 'Refused';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

export function statusTone(status: string): Tone {
  switch (status as SendStatus) {
    case 'sent':
      return 'good';
    case 'suppressed':
      return 'warn';
    case 'refused':
    case 'failed':
      return 'danger';
    default:
      return 'muted';
  }
}

/**
 * The provider's word, or `null` when there is nothing to say yet.
 *
 * Null rather than a "None" string so the caller decides how absence reads: a
 * queued send has no result because it has not gone, while a refused one never
 * will, and both read better as a muted "Waiting" or "Not sent" (see
 * `noDeliveryText`) than as a label pretending to be a result.
 */
export function deliveryLabel(delivery: string | null): string | null {
  switch (delivery as DeliveryStatus | null) {
    case null:
      return null;
    case 'delayed':
      return 'Delayed';
    case 'delivered':
      return 'Delivered';
    case 'failed':
      return 'Failed';
    case 'suppressed':
      return 'Suppressed by provider';
    case 'bounced':
      return 'Bounced';
    case 'complained':
      return 'Complained';
    default:
      return delivery;
  }
}

export function deliveryTone(delivery: string | null): Tone {
  switch (delivery as DeliveryStatus | null) {
    case 'delivered':
      return 'good';
    case 'delayed':
    case 'suppressed':
      return 'warn';
    case 'failed':
    case 'bounced':
    case 'complained':
      return 'danger';
    default:
      return 'muted';
  }
}

/** What a send with no delivery result says instead, by where it stopped. */
export function noDeliveryText(status: string): string {
  return status === 'queued' || status === 'sending' ? 'Waiting' : 'Not sent';
}

/**
 * The reason, as a sentence staff can repeat to somebody.
 *
 * The codes are auth's (`lib/notifications.ts`). A failed send with no reason is
 * the provider giving up after retries, and `lastError` carries the detail.
 */
export function reasonText(reason: string | null): string | null {
  if (!reason) return null;
  const known: Record<string, string> = {
    'opted-out': 'The recipient opted out of this kind of email.',
    'content-missing': 'The message content was missing when it came to be sent.',
    'too-many-attempts': 'It was picked up more times than the service allows, so it was given up rather than tried again.',
    'unknown-category': 'The sending app used a category the service does not know.',
    'expired-in-queue': 'It waited in the queue too long and was dropped.',
    'channel-not-built': 'The requested channel is not built yet.',
    'automated-run': 'It came from an automated test or CI run, which never sends email.',
    'undeliverable-address': 'The address is one that can never receive email.',
    'not-on-allowlist': 'This environment only sends to allowlisted addresses, and this one is not on the list.',
    'no-sending-identity': 'No sending identity is configured for this app.',
    'no-signing-secret': 'The unsubscribe link signing secret is not configured.',
    'no-opt-out-host': 'The unsubscribe host is not configured.',
    'no-postal-address': 'The postal address the footer needs is not configured.',
  };
  return known[reason] ?? reason;
}

export function sourceLabel(source: string): string {
  const known: Record<string, string> = {
    'one-click': 'One-click',
    'email-link': 'Email link',
    form: 'Form',
    complaint: 'Complaint',
  };
  return known[source] ?? source;
}

// ── Overview ─────────────────────────────────────────────────────────

/**
 * When a rate is highlighted (Jon, 2026-09-16: go with the mock-up, one level).
 *
 * COMPLAINTS AT 0.1% is the rate Google and Yahoo tell bulk senders to stay
 * under, and never to reach 0.3%. BOUNCES AT 2% is the common
 * industry line, and an address list above it is usually stale.
 *
 * Strictly ABOVE, so an app sitting exactly on a line is not flagged, and
 * applied to 7-day figures only, because that is what the summary reports.
 * One number each so changing the policy is a one-line diff.
 */
export const BOUNCE_RATE_WARN = 0.02;
export const COMPLAINT_RATE_WARN = 0.001;

/**
 * No highlight below this many sends, because a rate over a handful of emails
 * is noise: one bounce in ten sends is 10% and says nothing about the list.
 */
export const MIN_SENDS_FOR_RATE = 50;

export type RateFlags = { bounce: boolean; complaint: boolean };

export function rateFlags(row: NotificationSummary['deliverability'][number]): RateFlags {
  if (row.sent < MIN_SENDS_FOR_RATE) return { bounce: false, complaint: false };
  return {
    bounce: row.bounceRate > BOUNCE_RATE_WARN,
    complaint: row.complaintRate > COMPLAINT_RATE_WARN,
  };
}

/**
 * A fraction as a percentage people can compare: `3.7%`, `0.04%`, `0%`.
 *
 * Two significant decimals below 1% and one above, because complaint rates live
 * in hundredths of a percent and bounce rates in whole ones, and a single fixed
 * precision makes one of the two columns unreadable.
 */
export function percent(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return '0%';
  const p = fraction * 100;
  const text = p < 1 ? p.toFixed(2) : p.toFixed(1);
  return `${text.replace(/\.?0+$/, '')}%`;
}

/** The oldest queued message's age, coarse on purpose: `40 s`, `2 min`, `3 h`, `2 d`. */
export function queueAge(seconds: number | null): string {
  if (seconds === null) return 'None queued';
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h`;
  return `${Math.floor(seconds / 86400)} d`;
}

/**
 * A queue whose oldest message has waited this long is stuck rather than busy.
 * The longest retry wait is an hour, so anything past two is not a retry.
 */
export const STUCK_QUEUE_SECONDS = 2 * 60 * 60;

// ── Sections ─────────────────────────────────────────────────────────

export type NotificationSection = 'overview' | 'sends' | 'group-email' | 'opt-outs';

/** Group email sits after Sends, where its copies are logged (Jon, 2026-11-01). */
const ALL_SECTIONS = [
  { path: 'overview', label: 'Overview', needs: 'notification:read' },
  { path: 'sends', label: 'Sends', needs: 'notification:read' },
  { path: 'group-email', label: 'Group email', needs: 'notification:read' },
  { path: 'opt-outs', label: 'Opt-outs', needs: 'optOut:read' },
] as const;

/**
 * The tabs a session sees: each only with its permission (#131).
 *
 * `held` is the pair of answers from `useCan`, which are already optimistic
 * while capabilities are unknown, so this never hides a tab on a failed fetch.
 * An empty result means the role holds neither, and the screen says so.
 */
export function visibleSections(held: {
  notificationRead: boolean;
  optOutRead: boolean;
}): { path: NotificationSection; label: string }[] {
  return ALL_SECTIONS.filter((s) =>
    s.needs === 'notification:read' ? held.notificationRead : held.optOutRead,
  ).map(({ path, label }) => ({ path, label }));
}

/** The tab a path segment selects, or the first the session may see. */
export function resolveSection(
  visible: readonly { path: NotificationSection }[],
  segment?: string,
): NotificationSection | undefined {
  return visible.find((s) => s.path === segment)?.path ?? visible[0]?.path;
}

/**
 * Where the Sends tab lives, filtered to one address.
 *
 * The recent-sends panel on a user's profile links here, and the address is a
 * query parameter rather than a segment because it is a filter on the list, the
 * same call the Activity screen made for `?user=`.
 */
export function sendsPath(address?: string): string {
  return address ? `/notifications/sends?address=${encodeURIComponent(address)}` : '/notifications/sends';
}

/** Does this failure message come from a 404? See `describe()` in useAsync. */
export function isNotFound(message: string): boolean {
  return message.includes('(404)');
}

// ── Group email ──────────────────────────────────────────────────────

/**
 * Addresses as somebody types or pastes them: separated by commas,
 * semicolons, spaces or new lines, and `Name <a@b.co>` taken as its address.
 * Duplicates go, compared without case; nothing is validated here, because the
 * server says which entries are not addresses and the screen shows that.
 */
export function parseAddressList(text: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const bracketed = text.replace(/[^<>,;\n]*<([^<>]+)>/g, ' $1 ');
  for (const raw of bracketed.split(/[\s,;]+/)) {
    const value = raw.trim().replace(/^mailto:/i, '');
    if (!value || seen.has(value.toLowerCase())) continue;
    seen.add(value.toLowerCase());
    out.push(value);
  }
  return out;
}

export function groupEmailStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Draft';
    case 'scheduled':
      return 'Scheduled';
    case 'sending':
      return 'Sending';
    case 'sent':
      return 'Sent';
    default:
      return status;
  }
}

/**
 * What became of a sent email's copies, in the fewest words that answer "did
 * it go": `7 delivered, 1 skipped (opted out)`. Copies still on their way are
 * `waiting`; a copy the provider took but has not reported on is `sent`.
 */
export function outcomeSummary(o: {
  queued: number;
  sent: number;
  delivered: number;
  bounced: number;
  suppressed: number;
  refused: number;
  failed: number;
}): string {
  const parts: string[] = [];
  const undelivered = o.sent - o.delivered - o.bounced;
  if (o.delivered) parts.push(`${o.delivered} delivered`);
  if (undelivered > 0) parts.push(`${undelivered} sent`);
  if (o.queued) parts.push(`${o.queued} waiting`);
  if (o.bounced) parts.push(`${o.bounced} bounced`);
  if (o.suppressed) parts.push(`${o.suppressed} skipped (opted out)`);
  if (o.refused) parts.push(`${o.refused} refused`);
  if (o.failed) parts.push(`${o.failed} failed`);
  return parts.join(', ') || 'Nothing queued';
}

/**
 * The next `weekday` (0 Sunday to 6 Saturday) at `hour`:00 local time, at
 * least a minute after `now`. Today counts when the hour has not passed.
 */
export function nextAt(now: Date, weekday: number | 'tomorrow', hour: number): Date {
  const at = new Date(now);
  at.setSeconds(0, 0);
  at.setMinutes(0);
  at.setHours(hour);
  if (weekday === 'tomorrow') {
    at.setDate(at.getDate() + 1);
    return at;
  }
  let days = (weekday - at.getDay() + 7) % 7;
  if (days === 0 && at.getTime() - now.getTime() < 60_000) days = 7;
  at.setDate(at.getDate() + days);
  return at;
}

/** A Date as a `datetime-local` input's value, in local time. */
export function toLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** `Tue, Nov 3, 9:00 AM CST`, the way a schedule is read back. */
export function scheduleLabel(date: Date): string {
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

